/**
 * The Scoring pane — the table, then the seven rules that made it.
 *
 * The third column of the table names the rule behind each number, and those ids have to resolve
 * somewhere. Putting the rules on the SAME pane rather than sending the reader to a different
 * group is the point: "why is a two-point conversion worth that?" is answered by scrolling, not by
 * navigating away and losing the number you were asking about.
 *
 * The scoring values themselves are read from Yahoo and stored in `league-settings-2026.json`.
 * Nothing here retypes one.
 */

import leagueSettings from '../data/league-settings-2026.json';
import { rulesIn } from '../domain/ruleGroups.js';
import { lineageOf } from '../domain/ruleGroups.js';
import { Linkify } from './RuleText.js';

const SET = leagueSettings as {
  settings: string[][]; offense: string[][]; defense: string[][]; source: string;
};

export function ScoringPane() {
  const rules = rulesIn('scoring');
  return (
    <Linkify>
      <section className="scorepane">
        <div className="scoretables">
          <div className="scoreblock">
            <h3 className="scorecap">Offense</h3>
            <table className="scoretable">
              <thead>
                <tr><th>What</th><th className="num">Points</th><th>Where it came from</th></tr>
              </thead>
              <tbody>
                {SET.offense.map(([k, v, n]) => (
                  <tr key={k}><td>{k}</td><td className="num strong">{v}</td>
                    <td className="mut small">{n}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="scoreblock">
            <h3 className="scorecap">Defense and special teams</h3>
            <table className="scoretable">
              <thead>
                <tr><th>What</th><th className="num">Points</th><th>Where it came from</th></tr>
              </thead>
              <tbody>
                {SET.defense.map(([k, v, n]) => (
                  <tr key={k}><td>{k}</td><td className="num strong">{v}</td>
                    <td className="mut small">{n}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="note mut tight">{SET.source}</p>

        {/*
          The same rows the other groups render, so an id cited in the third column above resolves
          on this pane rather than sending the reader somewhere else to look it up.
        */}
        <h3 className="scorecap">The rules behind those numbers</h3>
        <ul className="rulerows">
          {rules.map((r) => {
            const line = lineageOf(r.id);
            return (
              <li key={r.id} id={r.id} className={`rulerow ${r.status}`}>
                <div className="rulerowhd static" data-nolink>
                  <span className="ridchip">{r.id}</span>
                  <span className="rtitle">{r.title}</span>
                </div>
                <p className="rsummary">{r.summary}</p>
                {line.replaces.length > 0 && (
                  <p className="rlineage">Replaces {line.replaces.map((x) => x.id).join(', ')}</p>
                )}
                <div className="rverbatim">{r.text}</div>
              </li>
            );
          })}
        </ul>
      </section>
    </Linkify>
  );
}
